import {
    select as D3select,
    dispatch as D3dispatch,
    scaleLinear as D3scaleLinear,
    scaleOrdinal as D3scaleOrdinal,
    scaleSqrt as D3scaleSqrt,
    scaleLog as D3scaleLog,
    arc as D3arc,
    hierarchy as D3hierarchy,
    partition as D3partition,
    min as D3min,
    sum as D3sum,
    interpolate as D3interpolate,
    format as D3format,
    color as D3color,
    bisector as D3bisector,
    brushX as D3brushX,
} from 'd3';
import D3Tooltips from './D3Tooltips.js';
import D3PiBrush from './D3PieBrush.js';

const noop = () => {};

const D3Hierarchy = function () {
    const dispatch = D3dispatch('click', 'sclick', 'rclick', 'mouseover', 'mouseleave');

    // overridable properties
    let theme;
    let width = 100;
    let height = 100;
    let z = D3scaleOrdinal();
    let type = 'circle';
    let scale = 'linear';
    let current;
    let duration = 300;
    let lduration = 80;
    let tooltip = D3Tooltips();

    const MARGIN = 20;
    const SUNBURST = 0;
    const ICICLE = 1;
    const RADIAL = 2;
    const HORIZ = 3;
    const layout = () => scale === 'linear'
        ? type === 'circle' ? SUNBURST : ICICLE
        : type === 'circle' ? RADIAL : HORIZ;

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            if (!data) return;

            // global scales
            const radius = D3min([width, (height - 2 * MARGIN)]) / 2;
            const innerRadius = 2 * radius / 5;
            let mheight;

            // start with the mini-map
            const x = D3scaleLinear();

            if (type === 'circle') {
                x.range([0, 2 * Math.PI]);
                // three circle mini map sizes, small medium and large
                mheight = innerRadius > 100 ? 40 : innerRadius > 50 ? 25 : 10;
            } else {
                x.range([0, width]);
                mheight = height > 200 ? 30 : height / 5;
            }

            let brush;
            if (type === 'circle') {
                brush = D3PiBrush()
                    .range([0, 1])
                    .innerRadius(mheight - 5)
                    .outerRadius(innerRadius + 5)
                    .on('start', noop);
            } else {
                brush = D3brushX().extent([[0, 0], [width, mheight]]);
            }
            const root = D3hierarchy(data)
                .sum(d => d.size)
                .sort((a, b) => b.value - a.value);

            const agents = D3partition()(root)
                .descendants()
                .filter(d => d.depth === 1);

            let arc;
            if (type === 'circle') {
                arc = D3arc()
                    .startAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x0))))
                    .endAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x1))))
                    .innerRadius(mheight)
                    .outerRadius(innerRadius);
            } else {
                // a fake arc function that actually draws rects the long way
                arc = d =>
                    'M' + x(d.x0) + ' ' + 0 +
                    ' H ' + x(d.x1) +
                    ' V ' + mheight +
                    ' H ' + x(d.x0) +
                    ' Z';
            }

            // DOM
            let chart = D3select(this).selectAll('.hierarchy').data([data]);
            chart.exit().remove();
            chart = chart.enter()
                .append('g')
                .attr('class', 'hierarchy')
                .merge(chart)
                .attr('transform', type === 'circle'
                    ? 'translate(' + (width / 2) + ',' + (MARGIN + height / 2) + ')'
                    : 'translate(' + MARGIN + ',' + MARGIN + ')');

            // add the tooltip last
            tooltip = tooltip.theme(theme);
            const tipj = D3select(this).selectAll('.hierarcy-tip').data([data]);
            tipj.enter().append('g')
                .attr('class', 'hierarcy-tip')
                .merge(tipj)
                .call(tooltip.bounds({ width: width + 2 * MARGIN, height }));
            tipj.exit().remove();

            let minimap = chart.selectAll('.minimap').data([agents]);
            // placement of popup menu
            minimap.enter().append('text').attr('class', 'popup').text('');
            minimap = minimap.enter()
                .append('g')
                .attr('class', 'minimap')
                .merge(minimap);

            // draw first level only
            const join = minimap.selectAll('.mini-segment')
                .data(agents, d => d.data.str);
            join.exit().remove();
            join.enter().append('path')
                .attr('class', 'mini-segment')
                .merge(join)
                .attr('fill-rule', 'evenodd')
                .style('fill', color)
                .style('stroke-width', '0.5px')
                .style('stroke', theme.palette.background.default)
                .attr('d', arc);

            brush
                .on('end', (e, d) => {
                    // custom pibrush passes parameters differently than the built-in brush
                    d = type === 'circle' ? d : (e.selection || x.range()).map(x.invert);
                    if (brush.snapping) return;
                    if (!d || agents.length < 1) return;

                    brush.snapping = true;

                    // snap to range excluding partial selections
                    let i0 = D3bisector(a => a.x0).left(agents, d[0]);
                    let i1 = D3bisector(a => a.x1).left(agents, d[1]);

                    // guard the edges
                    if (i1 >= agents.length) i1 = agents.length - 1;
                    if (i0 >= agents.length) i0 = 0;

                    // guard against empty selections
                    if (i1 === 0 && i0 === 0) {
                        i1 = agents.length - 1;
                        i0 = 0;
                    }

                    // at 12:00, no selection is the same as select all
                    if (i0 === i1 === 0) i1 = agents.length - 1;

                    current = [agents[i0].data.name, agents[i1].data.name];

                    // custom pibrush emits events differently than the built-in brush
                    if (type === 'circle') brush.move([agents[i0].x0, agents[i1].x1]);
                    else brushg.call(brush.move, [x(agents[i0].x0), x(agents[i1].x1)]);
                    brush.snapping = false;
                })
                .on('brush', applySelection);

            // brush added only once
            let brushg = chart.selectAll('.brush').data([{ t: type }], d => d.t);
            brushg.exit().remove();
            brushg = brushg.enter()
                .append('g')
                .attr('class', 'brush')
                .merge(brushg)
                .call(brush);

            // move brush to current selection
            let extent = [0, 1];
            if (current && current.length > 1) {
                const s0 = agents.find(a => a.data.name === current[0]) || { x0: 0 };
                const s1 = agents.find(a => a.data.name === current[1]) || { x1: 1 };
                // some kind of rounding bug
                if (s0.x0 === 0 && s1.x1 > 1) extent = [0, 1];
                else extent = [s0.x0, s1.x1];
            }
            if (type === 'circle') brush.move(extent);
            else brushg.call(brush.move, extent);

            function applySelection (e, d) {
                d = type === 'circle' ? d : (e.selection || x.range()).map(x.invert);
                const sel = agents.filter(a => {
                    if (d[1] < d[0]) return (a.x0 >= d[0]) || (a.x1 < d[1]);
                    return (a.x0 >= d[0]) && (a.x1 <= d[1]);
                });
                drawMainChart({
                    name: data.name,
                    size: data.size,
                    str: data.str,
                    children: (data.children || []).filter(c => sel.find(s => s.data.name === c.name))
                });
            }

            function drawMainChart (data) {
                const root = D3hierarchy(data)
                    .sum(d => d.size)
                    .sort((a, b) => b.value - a.value);
                let agents = D3partition()(root).descendants();

                // log scale does't actually plot a hierarchy
                if (scale === 'log') {
                    agents = agents.filter(d => d.depth === 1);
                    agents.forEach(d => { d.x1 = d.value });
                } else {
                    agents = agents.filter(d => d.depth > 0);
                }
                agents.forEach((a, i) => { a.index = i });

                // scales and arc generators
                const ZERO = scale === 'log' ? 0.00001 : 0;
                const mY = makeY();
                const mX = makeX();
                const mArc = makeArc();
                mArc.centroid = makeCentroid();

                function makeY () {
                    const h = height - MARGIN;
                    switch (layout()) {
                        case SUNBURST:
                            return D3scaleSqrt()
                                .range([ZERO, radius]);
                        case ICICLE:
                            return D3scaleLinear()
                                .range([-0.25 * h + mheight, h]);
                        case RADIAL:
                            return D3scaleLinear()
                                .domain([ZERO, agents.length])
                                .range([radius, innerRadius + MARGIN]);
                        case HORIZ:
                            return D3scaleLinear()
                                .domain([ZERO, agents.length])
                                .range([mheight + MARGIN, h]);
                    }
                }

                function makeX () {
                    switch (layout()) {
                        case SUNBURST:
                            return D3scaleLinear()
                                .range([ZERO, 2 * Math.PI]);
                        case ICICLE:
                            return D3scaleLinear()
                                .range([ZERO, width]);
                        case RADIAL:
                            return D3scaleLog()
                                .domain([ZERO, D3sum(agents, d => d.x1) || ZERO])
                                .range([ZERO, 2 * Math.PI]);
                        case HORIZ:
                            return D3scaleLog()
                                .domain([ZERO, D3sum(agents, d => d.x1) || ZERO])
                                .range([ZERO, width]);
                    }
                }

                function makeArc () {
                    const bh = (mY.range()[1] - mY.range()[0]) / (agents.length || 1);
                    switch (layout()) {
                        case SUNBURST:
                            return D3arc()
                                .startAngle(d => Math.max(ZERO, Math.min(2 * Math.PI, mX(d.x0))))
                                .endAngle(d => Math.max(ZERO, Math.min(2 * Math.PI, mX(d.x1))))
                                .innerRadius(d => Math.max(ZERO, mY(d.y0)))
                                .outerRadius(d => Math.max(ZERO, mY(d.y1)));
                        case ICICLE:
                            return d => {
                                return 'M' + mX(d.x0) + ' ' + mY(d.y0) +
                                ' H ' + mX(d.x1) +
                                ' V ' + mY(d.y1) +
                                ' H ' + mX(d.x0) +
                                ' Z';
                            };
                        case RADIAL:
                            return D3arc()
                                .startAngle(0)
                                .endAngle((d, i) => {
                                    return mX(d.x1 || ZERO);
                                })
                                .innerRadius((d, i) => mY(i))
                                .outerRadius((d, i) => mY(i) + bh);
                        case HORIZ:
                            return (d, i) => {
                                return 'M' + mX(ZERO) + ' ' + mY(i) +
                                ' H ' + mX(d.x1) +
                                ' v ' + bh +
                                ' H ' + mX(ZERO) +
                                ' Z';
                            };
                    }
                }

                function makeCentroid () {
                    const bh = (mY.range()[1] - mY.range()[0]) / agents.length;
                    switch (layout()) {
                        case SUNBURST:
                            return mArc.centroid;
                        case ICICLE:
                            return d => [mX(d.x0 + (d.x1 - d.x0) / 2), mY(d.y0 + (d.y1 - d.y0) / 2)];
                        case RADIAL:
                            return (d, i) => [mX(d.x1 || ZERO), -mY(i) - bh / 2];
                        case HORIZ:
                            return (d, i) => [MARGIN, mY(i) + bh / 2];
                    }
                }

                function tipPosition (d, i) {
                    const bh = (mY.range()[1] - mY.range()[0]) / agents.length;
                    switch (layout()) {
                        case SUNBURST:
                            return [width / 2 - 2 * MARGIN, MARGIN];
                        case ICICLE:
                            return [MARGIN + mX(d.x0 + (d.x1 - d.x0) / 2), mY(d.y0)];
                        case RADIAL:
                            return [MARGIN + width / 2, MARGIN + i * Math.abs(bh) - 5];
                        case HORIZ:
                            return [MARGIN * 2, mY(i) - 5];
                    }
                }

                // main added once and ensures that pie is always under the labels
                let main = chart.selectAll('.main').data([data]);
                main = main.enter()
                    .append('g')
                    .attr('class', 'main')
                    .merge(main);

                const join = main.selectAll('.segment')
                    .data(agents, function (d) { return d.data.str });
                    // .data(agents.map((a, i) => ({ ...a, index: i })), function (d) { return d.data.str });
                join.exit().remove();
                join.enter().append('path')
                    .attr('class', 'segment')
                    // start point for animation (each does not like arrow functions)
                    .each(function (d) { this._current = mX(ZERO) })
                    .merge(join)
                    .on('click', function (e, d) {
                        // actionListPopup dynamic placement is all over the place with circle segments.
                        // using a fixed element in the center is much safer
                        const jnode = chart.select('.popup')._groups[0];
                        dispatch.call('click', my, { d, elt: jnode });
                    })
                    .on('mouseover', mouseover)
                    .attr('fill-rule', 'evenodd')
                    .style('fill', color)
                    .style('opacity', 1)
                    // this transition is thebomb.com
                    .transition()
                    .delay(d => (d.depth - 1) * lduration)
                    .duration(d => duration)
                    .attrTween('d', arcTween)
                    .on('end', () => { chart.on('mouseleave', mouseleave) });

                // activate mouseleave once all the update animation is done
                chart.on('mouseleave', () => {});

                function arcTween (d, i) {
                    const interpolate = D3interpolate(this._current, d.x1);
                    this._current = scale === 'linear' ? ZERO : d.x1;
                    return t => mArc({
                        x0: d.x0,
                        x1: interpolate(t),
                        y0: d.y0,
                        y1: d.y1
                    }, i);
                }

                // animation functions
                function mouseover (e, d) {
                    const sequenceArray = (d.ancestors() || []).filter(d => d.depth > 0).reverse();

                    // highlight ancestors
                    chart.select('.main').selectAll('.segment')
                        .style('opacity', d => sequenceArray.indexOf(d) >= 0 ? 1 : 0.3);

                    dispatch.call('mouseover', my, d);
                    const p = tipPosition(d, d.index);
                    const app = sequenceArray.length ? sequenceArray[0].data.name : d.data.name;
                    let rest = sequenceArray.length > 1 ? sequenceArray[1].data.name : '';
                    rest += sequenceArray.length > 2 ? ' ' + sequenceArray[2].data.name : '';
                    tooltip.move(p[0], p[1], [[app, D3format(',.0f')(d.data.sum), rest]]);
                }
                function mouseleave (e, d) {
                    chart.select('.main').selectAll('.segment')
                        .transition()
                        .duration(100)
                        .style('opacity', 1);

                    dispatch.call('mouseleave', my, d);
                    tooltip.hide();
                }

                // labels - don't draw them on very small segments
                const trshld = type === 'circle'
                    ? scale === 'linear'
                        ? (d, i) => (d.x1 - d.x0) > (2.21 / radius)
                        : (d, i) => true
                    : (d, i) => (d.x1 - d.x0) > (10 / width);
                // we have two labels, foreground and background
                const lg = chart.selectAll('.labels').data([{ c: 'bkg' }, { c: 'frg' }], d => d.c);
                lg.enter()
                    .append('g')
                    .attr('class', 'labels')
                    .merge(lg)
                    // NOTE: l => {} doesn't work here. Some kind of compiler bug?
                    .each(function (l) {
                        const lbjoin = D3select(this).selectAll('.' + l.c)
                            .data(agents
                                .filter(d => d.depth === 1)
                                .filter(trshld), d => d.data.str);
                        lbjoin.exit().remove();
                        lbjoin.enter().append('text')
                            .attr('class', 'title ' + l.c)
                            .merge(lbjoin)
                            .text(d => d.data.name.substring(0, 20) + (d.data.name.length > 20 ? '...' : ''))
                            .attr('transform', (d, i) => {
                                d.ang = 90;
                                // rotate and flip if necessary
                                if (type === 'circle') {
                                    d.ang = ((mX((d.x0 + d.x1) / 2) - Math.PI / 2) / Math.PI * 180);
                                    d.ang += (d.ang > 90) ? 180 : 0;
                                }
                                if (scale === 'log') d.ang = 0;
                                return 'translate(' + mArc.centroid(d, i) + ')rotate(' + d.ang + ')';
                            })
                            .attr('text-anchor', d => d.ang > 90 ? 'end' : 'start')
                            .attr('dominant-baseline', 'middle')
                            .style('pointer-events', 'none')
                            .style('user-select', 'none')
                            .style('text-transform', 'capitalize')
                            .style('font-size', '12px')
                            .style('fill', theme.palette.text.primary)
                            .attr('opacity', 0)
                            // label show up after animation is done
                            .transition()
                            .delay(d => (d.depth - 1) * 10 + duration)
                            .attr('opacity', (d, i) => {
                                if (scale === 'linear') return 1;
                                // for log scales, we'll start skipping lables
                                const bh = (mY.range()[1] - mY.range()[0]) / agents.length;
                                const n = Math.abs(bh) > 0 ? 1 + Math.floor(11 / Math.abs(bh)) : agents.length;
                                return (i % n) ? 0 : 1;
                            });
                    });
            }

            // go lighter upwards (and darker sideways)
            function color (d) {
                let n = d;
                while (n.parent && n.parent.data.name !== 'All') n = n.parent;
                return D3color(z(n.data.name))
                    .brighter(d.depth < 2 ? 0 : (d.depth - 1) / 2)
                    // .darker(d.depth > 1 ? (d.x0 - d.parent.x0) * 6 : 0)
                    .hex();
            }
        });
    }

    // getters/setters
    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.height = function (v) {
        if (!arguments.length) return height;
        height = v;
        return my;
    };

    my.colors = function (v) {
        if (!arguments.length) return z;
        z = v;
        return my;
    };

    my.type = function (v) {
        if (!arguments.length) return type;
        type = v;
        return my;
    };

    my.scale = function (v) {
        if (!arguments.length) return scale;
        // clear current selection
        if (scale !== v) current = [];
        scale = v;
        return my;
    };

    my.animation = function (v) {
        if (!arguments.length) return duration;
        duration = v === 'fast'
            ? 300
            : v === 'slow'
                ? 800
                : 0;

        lduration = v === 'fast'
            ? 80
            : v === 'slow'
                ? 100
                : 0;

        return my;
    };

    my.clear = function () {
        current = [];
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    return my;
};

export default D3Hierarchy;
